import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
import { responsiveCellSize } from '../../../visuals/utils/responsiveCellSize';
/**
 * CommaShiftRenderer — Interactive komma-verschuiver voor ×10 / ÷10.
 *
 * Toont een kommagetal in plaatswaarde-cellen. De student verschuift de komma
 * stap voor stap naar links of rechts met ◀ / ▶ knoppen tot het antwoord
 * overeenkomt met de gevraagde bewerking (×10, ×100, ÷10, ÷100). De cijfers
 * blijven op hun plek staan — alleen de komma beweegt. Dit maakt de canonieke
 * misconceptie "×10 = nul erbij" expliciet zichtbaar als komma-shift.
 *
 * Didactisch anker:
 *  - Pluspunt / WiG: kommagetal × 10/100 = komma één/twee plekken naar rechts
 *  - Misconceptie: get.decimaal-komma-verschuiving
 *  - Tier-band: 6–10 (eind groep 6 t/m eind groep 7)
 *  - DOK: 2 (begrip toepassen van plaatswaarde-shift)
 *
 * Schema (data.commaShift):
 *   - startValue: '3,45'      — startgetal in Nederlandse notatie
 *   - operation: '×10' | '×100' | '÷10' | '÷100'
 *   - correctAnswer: '34,5'   — eindwaarde in Nederlandse notatie
 *   - showAnimation?: boolean — komma-shift animatie (default true)
 *   - acceptedAnswers?: string[] — extra accepteerbare schrijfwijzen
 *
 * Validator: string-normalisatie (comma → punt, trailing zeros gestript).
 */
interface CellModel {
  /** Display digit; '' for empty padding cell */
  digit: string;
  /** True for cells from the original number (vs. inserted padding) */
  isOriginal: boolean;
}
interface ParsedNumber {
  /** Digits-only string (no comma), with leading/trailing zeros preserved */
  digits: string;
  /** Original index of the comma in digits (0 = before first digit) */
  originalCommaIndex: number;
}
/**
 * Parse a Dutch decimal string into separate digit + comma-position parts.
 * Examples:
 *   '3,45'   -> { digits: '345', originalCommaIndex: 1 }
 *   '12'     -> { digits: '12',  originalCommaIndex: 2 } (no decimal part)
 *   '0,005'  -> { digits: '0005', originalCommaIndex: 1 }
 */
function parseStart(value: string): ParsedNumber {
  const normalized = value.replace('.', ',').trim();
  if (normalized.includes(',')) {
    const [intPart, fracPart] = normalized.split(',');
    return {
      digits: `${intPart}${fracPart}`,
      originalCommaIndex: intPart.length
    };
  }
  return {
    digits: normalized,
    originalCommaIndex: normalized.length
  };
}
/**
 * Format digits + comma-index back to a display string.
 * Strips trailing zeros after the comma (5,30 -> 5,3) and leading zeros
 * before the comma (007,5 -> 7,5) — but keeps a single 0 before the comma
 * (0,5 stays 0,5).
 */
function formatValue(digits: string, commaIndex: number): string {
  // Clamp commaIndex to valid range
  const idx = Math.max(0, Math.min(digits.length, commaIndex));
  let intPart = digits.slice(0, idx) || '0';
  let fracPart = digits.slice(idx);
  // Strip leading zeros from int part (keep at least one)
  intPart = intPart.replace(/^0+/, '') || '0';
  // Strip trailing zeros from fractional part
  fracPart = fracPart.replace(/0+$/, '');
  return fracPart.length > 0 ? `${intPart},${fracPart}` : intPart;
}
export const CommaShiftRenderer: React.FC<QuestionRendererProps> = ({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}) => {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.commaShift;
  const hasConfig = !!config && typeof config.startValue === 'string' && typeof config.operation === 'string' && typeof config.correctAnswer === 'string';
  // ── Parsed start ─────────────────────────────────────────────────────────
  // Need at least N padding cells on each side to allow the comma to move
  // through the full range for ×100 / ÷100 operations.
  const PADDING = 3;
  const parsed = useMemo<ParsedNumber>(() => parseStart(config?.startValue ?? '0'), [config?.startValue]);
  // Cell list: [pad..pad | digits | pad..pad]
  const cells: CellModel[] = useMemo(() => {
    const list: CellModel[] = [];
    for (let i = 0; i < PADDING; i++) {
      list.push({
        digit: '0',
        isOriginal: false
      });
    }
    for (const d of parsed.digits) {
      list.push({
        digit: d,
        isOriginal: true
      });
    }
    for (let i = 0; i < PADDING; i++) {
      list.push({
        digit: '0',
        isOriginal: false
      });
    }
    return list;
  }, [parsed.digits]);
  // Comma index is "the gap BEFORE cell N". Original gap is between
  // the padded prefix and the first original digit, offset by originalCommaIndex.
  const initialCommaIndex = PADDING + parsed.originalCommaIndex;
  // ── State ────────────────────────────────────────────────────────────────
  const [commaIndex, setCommaIndex] = useState<number>(initialCommaIndex);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef<HTMLDivElement>(null);
  // Reset on question change
  useEffect(() => {
    setCommaIndex(initialCommaIndex);
    setHasInteracted(false);
  }, [question.id, initialCommaIndex]);
  // Ready when student has moved the comma at least once
  useEffect(() => {
    onReadyChange?.(hasInteracted);
  }, [hasInteracted, onReadyChange]);
  // ResizeObserver — MUST live before the conditional early-return below to
  // respect React Rules of Hooks. Was previously placed after the bail-out
  // which caused a hook-order crash whenever config flipped.
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  // ── Derived display value ────────────────────────────────────────────────
  // Take the display digits — strip padding-zero positions that aren't between
  // the comma and an original digit. We pass the FULL digit string + comma
  // index to formatValue, which already strips redundant leading/trailing
  // zeros, so the readout matches everyday Dutch notation.
  const displayDigits = cells.map(c => c.digit).join('');
  const currentValue = useMemo(() => {
    return formatValue(displayDigits, commaIndex);
  }, [displayDigits, commaIndex]);
  // ── Interaction ──────────────────────────────────────────────────────────
  const canShiftLeft = commaIndex > 1; // leave at least one digit before comma
  const canShiftRight = commaIndex < cells.length;
  const shift = useCallback((delta: -1 | 1) => {
    hapticManager.hapticLight();
    setHasInteracted(true);
    setCommaIndex(prev => {
      const next = prev + delta;
      if (next < 1 || next > cells.length) return prev;
      return next;
    });
  }, [cells.length]);
  const reset = useCallback(() => {
    hapticManager.hapticLight();
    setCommaIndex(initialCommaIndex);
    setHasInteracted(false);
  }, [initialCommaIndex]);
  // ── Edge-triggered submit ────────────────────────────────────────────────
  useEdgeTriggeredSubmit(currentValue, shouldValidate, onAnswer, question.id);
  // ── Bail for malformed config ────────────────────────────────────────────
  if (!hasConfig || !config) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-10347">
        <p className="text-amber-700 font-bold" data-id="element-10348">
          ⚠️ Configuratie ontbreekt (commaShift)
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-10349">
          Deze vraag mist <code data-id="element-10350">data.commaShift</code> met{' '}
          <code data-id="element-10351">startValue</code>, <code data-id="element-10352">operation</code> en{' '}
          <code data-id="element-10353">correctAnswer</code>.
        </p>
      </div>;
  }
  // ── Layout constants ─────────────────────────────────────────────────────
  const CELL_GAP = 4; // px between cells (the comma sits in this gap)
  const availableWidth = containerWidth - 32 - (cells.length - 1) * CELL_GAP; // 32px for padding
  const CELL_SIZE = responsiveCellSize(cells.length, availableWidth, 56, 28); // px
  const totalWidth = cells.length * CELL_SIZE + (cells.length - 1) * CELL_GAP;
  // Comma x-position: sits in the gap BEFORE cells[commaIndex].
  // For commaIndex == 0: at the very left (before first cell).
  // For commaIndex == cells.length: at the very right (after last cell).
  const commaX = commaIndex * CELL_SIZE + (commaIndex - 0.5) * CELL_GAP - CELL_GAP / 2;
  // Determine which cells are "visible" — strip cells that are pure padding
  // outside of the meaningful range. We dim padding cells visually but keep
  // them in DOM so the comma can travel through them.
  const visibleStart = PADDING - 1;
  const visibleEnd = PADDING + parsed.digits.length;
  return <div className="w-full max-w-2xl mx-auto space-y-5" data-id="element-10354">
      {/* Operation prompt header */}
      <div className="rounded-2xl p-4 sm:p-5 border-2" style={{
      backgroundColor: hexToRgba(color, 0.07),
      borderColor: hexToRgba(color, 0.25)
    }} data-id="element-10355">
        <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-10356">
          <div className="flex items-baseline gap-3 flex-wrap" data-id="element-10357">
            <span className="text-3xl sm:text-4xl font-black tabular-nums text-slate-800" data-id="element-10358">
              {config.startValue}
            </span>
            <span className="text-2xl sm:text-3xl font-black px-3 py-1 rounded-xl" style={{
            backgroundColor: color,
            color: 'white'
          }} data-id="element-10359">
              {config.operation}
            </span>
            <span className="text-3xl sm:text-4xl font-black text-slate-400" data-id="element-10360">
              =
            </span>
            <span className="text-3xl sm:text-4xl font-black tabular-nums px-3 py-1 rounded-xl" style={{
            backgroundColor: hasInteracted ? hexToRgba(color, 0.15) : '#f1f5f9',
            color: hasInteracted ? color : '#94a3b8',
            minWidth: 90,
            textAlign: 'center'
          }} data-id="element-10361">
              {hasInteracted ? currentValue : '?'}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3" data-id="element-10362">
          Schuif de komma naar de juiste plek met ◀ en ▶. De cijfers blijven
          staan — alleen de komma beweegt.
        </p>
      </div>

      {/* Cell grid with sliding comma */}
      <div ref={containerRef} className="bg-white rounded-2xl p-4 sm:p-6 border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-10363">
        <div className="flex justify-center min-w-fit" data-id="element-10364">
          <div className="relative" style={{
          width: totalWidth,
          height: CELL_SIZE + 24
        }} data-id="element-10365">
            {/* Cells row */}
            <div className="flex items-center" style={{
            gap: CELL_GAP
          }} data-id="element-10366">
              {cells.map((cell, i) => {
              const isVisible = i >= visibleStart && i <= visibleEnd;
              const isOriginalDigit = cell.isOriginal;
              return <div key={i} className="flex items-center justify-center rounded-xl font-black tabular-nums select-none transition-colors" style={{
                width: CELL_SIZE,
                height: CELL_SIZE,
                fontSize: CELL_SIZE * 0.5,
                backgroundColor: isOriginalDigit ? '#f8fafc' : 'transparent',
                border: isOriginalDigit ? '2px solid #e2e8f0' : '2px dashed #e2e8f0',
                color: isOriginalDigit ? '#1e293b' : isVisible ? '#cbd5e1' : '#f1f5f9'
              }} aria-hidden={!isVisible} data-id="element-10367">
                    {cell.digit}
                  </div>;
            })}
            </div>

            {/* Floating comma — animates between cell gaps */}
            <motion.div className="absolute flex items-end justify-center pointer-events-none" style={{
            width: 24,
            height: CELL_SIZE,
            top: 0,
            left: 0,
            marginLeft: -12
          }} animate={{
            x: commaX + 12
          }} transition={{
            type: 'spring',
            stiffness: 320,
            damping: 26
          }} data-id="element-10368">
              <div className="flex items-end justify-center" style={{
              width: 24,
              height: CELL_SIZE,
              paddingBottom: 6
            }} data-id="element-10369">
                <motion.div className="rounded-full" style={{
                width: 14,
                height: 14,
                backgroundColor: color,
                boxShadow: `0 4px 12px ${hexToRgba(color, 0.5)}`
              }} animate={{
                scale: hasInteracted ? [1, 1.25, 1] : 1
              }} transition={{
                duration: 0.35
              }} key={commaIndex} // restart pulse on each shift
              data-id="element-10370" />
              </div>
            </motion.div>

            {/* Original-comma ghost marker — shows where it started */}
            {hasInteracted && <div className="absolute pointer-events-none flex items-end justify-center" style={{
            width: 24,
            height: CELL_SIZE,
            top: 0,
            left: initialCommaIndex * CELL_SIZE + (initialCommaIndex - 0.5) * CELL_GAP - CELL_GAP / 2 - 12 + 12,
            paddingBottom: 6
          }} data-id="element-10371">
                <div className="rounded-full" style={{
              width: 10,
              height: 10,
              backgroundColor: '#cbd5e1',
              opacity: 0.6
            }} aria-label="Beginpositie van de komma" data-id="element-10372" />
              </div>}
          </div>
        </div>
      </div>

      {/* Shift controls — sibling of the cell-grid card so they're not clipped */}
      <div className="flex flex-col items-center gap-3" data-id="element-10373">
        <div className="flex items-center justify-center gap-3 w-full" data-id="element-10374">
          <button type="button" onClick={() => shift(-1)} disabled={!canShiftLeft} className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-95 touch-manipulation" style={{
          backgroundColor: color,
          boxShadow: canShiftLeft ? `0 4px 12px ${hexToRgba(color, 0.35)}` : 'none'
        }} aria-label="Komma één plaats naar links" data-id="element-10375">
            <ChevronLeft className="w-5 h-5" data-id="element-10376" />
            <span className="hidden sm:inline" data-id="element-10377">Links</span>
          </button>

          <div className="flex flex-col items-center px-3" data-id="element-10378">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-10379">
              Verschuiving
            </span>
            <span className="text-lg font-black tabular-nums" style={{
            color
          }} data-id="element-10380">
              {commaIndex - initialCommaIndex > 0 ? `+${commaIndex - initialCommaIndex}` : commaIndex - initialCommaIndex}
            </span>
          </div>

          <button type="button" onClick={() => shift(1)} disabled={!canShiftRight} className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-95 touch-manipulation" style={{
          backgroundColor: color,
          boxShadow: canShiftRight ? `0 4px 12px ${hexToRgba(color, 0.35)}` : 'none'
        }} aria-label="Komma één plaats naar rechts" data-id="element-10381">
            <span className="hidden sm:inline" data-id="element-10382">Rechts</span>
            <ChevronRight className="w-5 h-5" data-id="element-10383" />
          </button>
        </div>

        {/* Reset link */}
        <AnimatePresence data-id="element-10384">
          {hasInteracted && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="flex justify-center mt-3" data-id="element-10385">
              <button type="button" onClick={reset} className="text-xs font-semibold text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline transition-colors" data-id="element-10386">
                Begin opnieuw
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
};